import { formatExternalUrl } from '@/utils/formatExternalUrl';
import { Button, Flex, Heading, Text, type FlexProps } from '@chakra-ui/react';
import { MotionBox } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { ActiveItemWrapper } from '../../Editor/components/ActiveItemWrapper';

type Props = {
  activeItem?: string;
  isPreview?: boolean;
} & PrismaJson.CollectionFooter &
  Omit<FlexProps, 'title'>;

export const FooterBlock = ({
  title,
  description,
  link,
  activeItem,
  isPreview,
  ...props
}: Props) => {
  const { t } = useTranslation('collection');

  return (
    <Flex
      pt={{ base: 'space.40', md: 'space.96' }}
      pb={isPreview ? { base: 'space.40', md: 'space.96' } : { base: 'space.88', md: rem(176) }}
      px={{ base: 'space.32', md: 'space.96' }}
      bg="black"
      color="white"
      align="center"
      flexDir="column"
      textAlign="center"
      roundedBottom={isPreview ? 'radius.32' : undefined}
      {...props}
    >
      <MotionBox
        initial={{ opacity: isPreview ? 1 : 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ amount: 1, once: true }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <Heading
          fontSize={{ base: '2xl', md: '5xl', lg: '6xl' }}
          mb={{ base: 'space.32', md: 'space.40', lg: 'space.48' }}
          bg={activeItem === 'title' ? 'arcticBlue' : 'unset'}
          color={activeItem === 'title' ? 'white' : 'inherit'}
          transition="all 120ms ease-in-out"
          letterSpacing="title"
          lineHeight="sm"
        >
          {title}
        </Heading>
      </MotionBox>
      <MotionBox
        initial={{ opacity: isPreview ? 1 : 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ amount: 1, once: true }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <Text
          maxWidth={rem(840)}
          fontSize={{ base: 'lg', md: 'xl' }}
          mb={{ base: 'space.32', md: 'space.40', lg: 'space.48' }}
          bg={activeItem === 'text' ? 'arcticBlue' : 'unset'}
          color={activeItem === 'text' ? 'white' : 'inherit'}
          transition="all 120ms ease-in-out"
        >
          {description}
        </Text>
      </MotionBox>
      <MotionBox
        initial={{ opacity: isPreview ? 1 : 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ amount: 1, once: true }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <ActiveItemWrapper
          rounded="full"
          isActive={activeItem === 'hyperlink'}
          isPreview={isPreview}
        >
          {(isPreview || link?.value) && (
            <Button
              as="a"
              href={formatExternalUrl(link?.value || undefined)}
              variant="secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              {link?.label || t('footer.cta-label')}
            </Button>
          )}
        </ActiveItemWrapper>
      </MotionBox>
    </Flex>
  );
};
